// src/services/api/endpoints.js
import api from './index';
import axios from 'axios';
import { getAuthApiUrl, getWeaversWorldApiUrl, getCsrfToken, getBaseUrl } from '../utils';

// Character endpoints
export const characterApi = {
  getAll: () => api.get('/characters/'),
  create: (characterData, activeChildProfileId = null) => {
    const config = {};
    
    // If an active child profile ID is provided, include it in a custom header
    if (activeChildProfileId) {
      config.headers = {
        'X-Active-Child-Profile-ID': activeChildProfileId
      };
    }
    
    return api.post('/characters/', characterData, config);
  },
  update: (characterId, characterData) => api.put(`/characters/${characterId}/`, characterData),
  delete: (characterId) => api.delete(`/characters/${characterId}/`),
  getById: (characterId) => api.get(`/characters/${characterId}/`),
};

// Story endpoints
export const storyApi = {
  create: (storyData) => api.post('/stories/', storyData),
  getAll: (options = {}) => api.get('/stories/', options),
  getById: (storyId, options = {}) => api.get(`/stories/${storyId}/`, options),
  // Use the standard api client with properly configured interceptors and auth
  getState: (storyId, options = {}) => api.get(`/story/${storyId}/state/`, options),
  generatePage: (storyId) => api.post(`/stories/${storyId}/pages/`),
  toggleSharing: (storyId) => api.post(`/stories/${storyId}/toggle-sharing/`),
};

// Page endpoints
export const pageApi = {
  // Using direct fetch for images to avoid axios transformation issues
  getImage: (pageId) => fetch(`${api.defaults.baseURL}/pages/${pageId}/image/`)
    .then(response => {
      if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
      return response.blob();
    })
    .then(blob => URL.createObjectURL(blob)),
};

// Create auth API client
const authApiClient = axios.create({
  baseURL: getAuthApiUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add CSRF token to auth requests
authApiClient.interceptors.request.use(config => {
  if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
    config.headers['X-CSRFToken'] = getCsrfToken();
  }
  return config;
});

// Create API client for child profiles
const childProfilesApiClient = axios.create({
  baseURL: getWeaversWorldApiUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add CSRF token to child profile requests
childProfilesApiClient.interceptors.request.use(config => {
  if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
    config.headers['X-CSRFToken'] = getCsrfToken();
  }
  return config;
});

// Authentication endpoints
export const authApi = {
  // User data
  getCurrentUser: () => authApiClient.get('/accounts/api/user/'),
  getProfile: () => authApiClient.get('/accounts/api/profile/'),
  updateProfile: (profileData) => authApiClient.patch('/accounts/api/profile/', profileData),
  
  // Authentication - now using our API endpoints
  login: (credentials) => authApiClient.post('/accounts/api/login/', credentials),
  logout: () => authApiClient.post('/accounts/api/logout/'),
  register: (userData) => authApiClient.post('/accounts/api/signup/', userData),
  
  // Password management
  resetPassword: (email) => authApiClient.post('/accounts/api/password/reset/', { email }),
  confirmResetPassword: (data) => authApiClient.post('/accounts/api/password/reset/confirm/', data),
  
  // Child profiles
  getChildProfiles: () => childProfilesApiClient.get('/child-profiles/'),
  createChildProfile: (profileData) => childProfilesApiClient.post('/child-profiles/', profileData),
  updateChildProfile: (profileId, profileData) => childProfilesApiClient.patch(`/child-profiles/${profileId}/`, profileData),
  deleteChildProfile: (profileId) => childProfilesApiClient.delete(`/child-profiles/${profileId}/`),
  
  // Session migration
  migrateSession: (childProfileId) => childProfilesApiClient.post(`/migrate-session/${childProfileId}/`)
};

// Export all APIs as a single object
const apiEndpoints = {
  character: characterApi,
  story: storyApi,
  page: pageApi,
  auth: authApi,
};

export default apiEndpoints;